import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Tooltip,
  Box,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Link,
  Chip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import LockIcon from '@mui/icons-material/Lock';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
// Removed import { styled } from '@mui/material/styles'; as it might not be needed if no other styled components exist
import {
  DataGrid,
  GridColDef,
  GridRowsProp,
  GridRenderCellParams,
  GridAlignment,
} from '@mui/x-data-grid'; // Import DataGrid
import {
  COLOR_GREY_600,
  COLOR_GREY_800,
  COLOR_PRIMARY_2,
  COLOR_PRIMARY_2_800,
  COLOR_TEXT_1,
  COLOR_TEXT_GREY,
} from 'src/colors';
import { useUpgradeMesageExpertFeature } from 'src/hooks/useUpgradeText';
import {
  abbreviateNumber,
  convertCurrency,
  filterExampleSavedMarkets,
  formatCurrency,
  getGradeColor,
} from 'src/utils/functions';
import {
  COLOR_GREEN,
  COLOR_PRIMARY,
  exchangeRates,
  MARKET_FINDER_COLUMN_METADATA_ORDER,
  SAVED_MARKET_FREE_USER_LIMIT,
  SAVED_MARKET_RE_DIRECTION_MODAL_CLOSED_KEY,
  SAVED_MARKETS_PAGE_URL,
  USER_WISE_SAVE_LIST_LIMIT,
} from 'src/const';
import { LoadingCard } from 'src/App/HOC/Cards';
import { updateMarketInList, setOpenSavedMarketsCTAModalAction } from 'src/store/Dashboard/actions';
import { useDispatch, useSelector } from 'react-redux';

// Added imports for Modal and CloseIcon
import Modal from '@mui/material/Modal';
import CloseIcon from '@mui/icons-material/Close';
import { useHistory } from 'react-router-dom';
import TooltipNew from 'src/App/HOC/Tooltips/TooltipNew';
import { RED_BUTTON_COLOR } from 'src/colors';
import { POINTER_GREEN } from 'src/const';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ConfirmationModal from 'src/App/Modals/ConfirmationModal';
import SavedMarketListsDropdown from '../OverviewPage/HOC/Filters/SavedMarketListPage/SavedMarketListsDropdown';
import { User } from 'src/interfaces/iuser';
// Define blur states (copied from MarketExplorerPage for encapsulation)
type BlurState = 'none' | 'blurMarket' | 'blurAll' | 'blurPartial';

// Define the CSS styles for blurring
const blurStyle = {
  filter: 'blur(4px)',
  userSelect: 'none',
  pointerEvents: 'none',
} as const; // Use 'as const' for stricter typing of style keys

// Define the structure for metadata (Import or define if not already)
interface ColumnMetadata {
  unit: string | null;
  is_currency: boolean;
  display_name: string;
  is_sortable: boolean;
  data_type: string;
  width?: number;
}

interface MarketFinderMetadata {
  [key: string]: ColumnMetadata;
}

// Define the structure for the data rows (Adjust based on DUMMY_MARKET_FINDER_DATA)
export interface MarketDataRow {
  rank: number | string; // Allow rank to be number or string (for 'locked')
  name: string;
  short_term_rental_yield?: number;
  rent_gap?: number;
  occupancy_rate?: number;
  adr?: number;
  revenue?: number;
  supply?: number;
  regulation?: string; // Or appropriate type
  yoy_supply_change?: number;
  yoy_revenue_change?: number;
  [key: string]: any; // Allow other properties
}

// Tooltip mapping based on your initial request
const columnTooltips: { [key: string]: string } = {
  short_term_rental_yield:
    'Ratio of annual Airbnb income to property price. Higher yield means better ROI.',
  rent_gap:
    'Difference between long-term and short-term rental returns. Larger gaps often mean higher Airbnb profit potential.',
  revenue: 'Median annual revenue generated by 2-bedroom listings in this market.',
  regulation:
    "This is based on the percentage of listings that include license details. While it's a useful signal, it may not always reflect actual regulation strictness or enforcement.",
  yoy_supply_change:
    'Year-over-year change in the number of active listings. Indicates market saturation or growth.',
  yoy_revenue_change:
    'Year-over-year change in median listing revenue. Helps spot improving or declining markets.',
};

// Tooltip helper component
const HeaderWithTooltip = ({ label, tooltip }: { label: string; tooltip: string }) => (
  <Box display="flex" alignItems="center" gap={0.5} sx={{ paddingRight: '30px' }}>
    {/* Added bold font weight directly here */}
    <Typography variant="inherit" component="span" sx={{ fontWeight: 'bold' }}>
      {label}
    </Typography>
    <Tooltip title={tooltip} arrow placement="top">
      <InfoOutlinedIcon sx={{ fontSize: '1rem', color: COLOR_TEXT_GREY }} />
    </Tooltip>
  </Box>
);

// Helper function to format cell values for DataGrid
const formatCellValue = (
  value: any,
  metadata: ColumnMetadata | undefined,
  currency: string,
  currencySymbol: string
): string => {
  if (value === null || value === undefined || value === '-') return '-';

  let formattedValue: string | number = value;
  let isCurrencyField = metadata?.is_currency;

  // If the value is 'locked' (for rank), return it directly to be handled by CellContent
  if (value === 'locked') {
    return 'locked';
  }

  // Format numbers with locale string and handle percentages/currency
  if (metadata && (metadata.data_type === 'number' || metadata.data_type === 'integer')) {
    try {
      const numberValue = Number(value);
      if (!isNaN(numberValue)) {
        // Convert currency first if applicable
        const valueToFormat = isCurrencyField
          ? convertCurrency(currency, numberValue)
          : numberValue;

        formattedValue = valueToFormat.toLocaleString(undefined, {
          // Use minimumFractionDigits for currency, allowing decimals
          minimumFractionDigits: isCurrencyField ? 2 : metadata.unit === '%' ? 1 : 0,
          maximumFractionDigits: isCurrencyField ? 2 : metadata.unit === '%' ? 1 : 0,
        });
      } else {
        formattedValue = '-'; // Handle non-numeric gracefully
      }
    } catch (e) {
      formattedValue = '-'; // Handle errors during conversion
    }
  } else if (typeof value === 'number') {
    try {
      // Format standalone numbers like 'rank'
      formattedValue = value.toLocaleString();
    } catch (e) {
      formattedValue = String(value); // Fallback if toLocaleString fails
    }
  } else {
    formattedValue = String(value); // Ensure string conversion for non-numeric types
  }

  // Apply currency/percentage symbols AFTER formatting
  if (isCurrencyField) {
    // Use the passed currencySymbol
    formattedValue = `${currencySymbol}${formattedValue}`;
  } else if (metadata?.unit === '%') {
    formattedValue = `${formattedValue}%`;
  }

  // Fix: Make display name check case-insensitive for 'Rent Gap'
  if (metadata?.display_name && metadata.display_name.toLowerCase() === 'rent gap') {
    formattedValue = `${formattedValue}/mo`;
  }

  if (
    metadata?.display_name &&
    (metadata.display_name.toLowerCase() === 'average revenue (2 bd)' ||
      metadata.display_name.toLowerCase() === 'rent gap')
  ) {
    const originalNumericValue = Number(value);
    if (!isNaN(originalNumericValue)) {
      const valueForAbbreviation = isCurrencyField
        ? convertCurrency(currency, originalNumericValue)
        : originalNumericValue;
      formattedValue = `${currencySymbol}${abbreviateNumber(valueForAbbreviation)}/yr`;
    } else {
      formattedValue = '-';
    }
  }

  return String(formattedValue);
};

// Apply blur based on state and column
const CellContent = ({
  params,
  columnMeta,
  currency,
  currencySymbol,
  handleOnSampleClick,
}: {
  params: GridRenderCellParams;
  columnMeta?: ColumnMetadata;
  currency: string;
  currencySymbol: string;
  handleOnSampleClick: (sampleType: string) => void;
}) => {
  const value = params.value;
  const field = params.field;

  // Check if the value is 'locked'
  const isLockedValue = value === 'locked';

  // Special rendering for rank if it's the locked icon
  if (field === 'rank' && isLockedValue) {
    return (
      <IconButton onClick={() => handleOnSampleClick('expert')}>
        <LockIcon sx={{ color: COLOR_TEXT_GREY }} />
      </IconButton>
    );
  }

  // Format the value
  const formattedValue = isLockedValue
    ? 'locked'
    : formatCellValue(value, columnMeta, currency, currencySymbol);

  // Special handling for 'name' column to allow wrapping
  if (field === 'name') {
    // Show country_code after name if available
    const countryCode = params.row.country_code;
    const marketUrl = params.row.market_url;
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          whiteSpace: 'normal',
          lineHeight: 'normal',
          ...(isLockedValue && blurStyle),
        }}
      >
        <Link
          href={marketUrl}
          target="_blank"
          sx={{
            display: 'flex',
            alignItems: 'center',
            minWidth: 0,
            flex: 1,
            overflow: 'hidden',
            color: COLOR_TEXT_1,
            textDecoration: 'none',
          }}
        >
          <img
            src={`https://flagcdn.com/w80/${countryCode?.toLowerCase()}.png`}
            alt="Country flag"
            style={{
              width: '30px',
              height: '20px',
              objectFit: 'cover',
              borderRadius: '4px',
              marginRight: '8px',
            }}
          />
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {formattedValue}
          </Typography>
        </Link>
        {!isLockedValue && marketUrl && (
          <Button
            size="small"
            variant="outlined"
            sx={{
              ml: 1,
              minWidth: '60px',
              padding: '2px 10px',
              fontSize: '10px',
              textTransform: 'none',
              borderRadius: '900px',
            }}
            onClick={e => {
              e.stopPropagation();
              window.open(marketUrl, '_blank', 'noopener,noreferrer');
            }}
          >
            Analyze
          </Button>
        )}
      </Box>
    );
  }

  // Custom rendering for regulation cell
  if (field === 'regulation') {
    const status = (value || '').toLowerCase();
    let bgColor = '#efefef';
    let textColor = '#555';
    if (status === 'strict') {
      bgColor = RED_BUTTON_COLOR + '22';
      textColor = RED_BUTTON_COLOR;
    } else if (status === 'lenient') {
      bgColor = COLOR_GREEN + '22';
      textColor = COLOR_GREEN;
    }
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          background: bgColor,
          borderRadius: '900px',
          padding: '4px 8px',
          color: textColor,
          textTransform: 'capitalize',
          width: 'fit-content',
        }}
      >
        {formattedValue}
      </Box>
    );
  }

  if (field === 'market_grade') {
    return <Box sx={{ color: getGradeColor(value), fontWeight: 'bold' }}>{formattedValue}</Box>;
  }

  // Default rendering for other cells, apply blur style if value is 'locked'
  return <span style={isLockedValue ? blurStyle : {}}>{formattedValue}</span>;
};

const BlurCTA = ({
  handleOnSampleClick,
  totalRows,
}: {
  handleOnSampleClick;
  totalRows: number;
}) => {
  const upgradeMessage = useUpgradeMesageExpertFeature();
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      sx={{
        position: 'absolute',
        top: `${Math.min(totalRows, 3) * 67}px`,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '100%',
        height: '150px',
        backgroundColor: '#fff0',
        zIndex: 1000,
        padding: '20px',
        borderRadius: '12px',
        pointerEvents: 'none',
      }}
    >
      <Button
        variant="contained"
        onClick={() => handleOnSampleClick('expert')}
        sx={{
          color: '#fff',
          pointerEvents: 'auto',
          backgroundColor: COLOR_PRIMARY_2_800,
          boxShadow: '0px 6px 10px 0px rgba(0, 0, 0, 0.2)',
        }}
        startIcon={<LockOpenOutlinedIcon />}
      >
        {upgradeMessage}
      </Button>
    </Box>
  );
};

const getFilteredMetadataKeys = (
  metadata: MarketFinderMetadata,
  selectedSortBy: string,
  userPickedColumnFields: string[]
) => {
  let baseKeys: string[];

  if (userPickedColumnFields && userPickedColumnFields.length > 0) {
    baseKeys = MARKET_FINDER_COLUMN_METADATA_ORDER.filter(
      key => userPickedColumnFields.includes(key) && metadata.hasOwnProperty(key)
    );
  } else {
    baseKeys = MARKET_FINDER_COLUMN_METADATA_ORDER.filter(key => metadata.hasOwnProperty(key));
  }

  let filteredKeys = [...baseKeys];

  if (selectedSortBy === 'short_term_rental_yield') {
    // Top Rental Arbitrage
    // Ensure short_term_rental_yield is present
    if (
      !filteredKeys.includes('short_term_rental_yield') &&
      metadata.hasOwnProperty('short_term_rental_yield')
    ) {
      filteredKeys.push('short_term_rental_yield');
    }
    // Remove rent_gap if present
    filteredKeys = filteredKeys.filter(key => key !== 'rent_gap');
  } else if (selectedSortBy === 'rent_gap') {
    // Top Investment
    // Ensure rent_gap is present
    if (!filteredKeys.includes('rent_gap') && metadata.hasOwnProperty('rent_gap')) {
      filteredKeys.push('rent_gap');
    }
    // Remove short_term_rental_yield if present
    filteredKeys = filteredKeys.filter(key => key !== 'short_term_rental_yield');
  } else {
    // For other sort types, if neither specific yield/gap is selected, remove both if they were not in userPickedColumnFields
    if (userPickedColumnFields && userPickedColumnFields.length > 0) {
      // Already handled by baseKeys logic using userPickedColumnFields
    } else {
      // If no userPickedColumnFields, and not a specific sort, remove both yield and gap
      // This case might need refinement based on desired default behavior when no columns are picked and no specific sort is active.
      // For now, let's assume if no user columns and no specific sort, we show what MARKET_FINDER_COLUMN_METADATA_ORDER defines, excluding these two unless explicitly part of it.
      filteredKeys = filteredKeys.filter(
        key => key !== 'rent_gap' && key !== 'short_term_rental_yield'
      );
    }
  }

  // Deduplicate keys after potential pushes
  let finalKeys = Array.from(new Set(filteredKeys));

  // Preserve the order from MARKET_FINDER_COLUMN_METADATA_ORDER for the keys that are present in finalKeys.
  finalKeys = MARKET_FINDER_COLUMN_METADATA_ORDER.filter(key => finalKeys.includes(key));

  // Reordering logic: Place 'rent_gap' or 'short_term_rental_yield' before 'supply' if applicable
  const supplyKey = 'supply';
  let keyToMove: string | null = null;

  if (selectedSortBy === 'rent_gap' && finalKeys.includes('rent_gap')) {
    keyToMove = 'rent_gap';
  } else if (
    selectedSortBy === 'short_term_rental_yield' &&
    finalKeys.includes('short_term_rental_yield')
  ) {
    keyToMove = 'short_term_rental_yield';
  }

  if (
    keyToMove &&
    finalKeys.includes(supplyKey) &&
    finalKeys.indexOf(keyToMove) > finalKeys.indexOf(supplyKey)
  ) {
    const tempKeys = finalKeys.filter(k => k !== keyToMove);
    const indexOfSupply = tempKeys.indexOf(supplyKey);
    if (indexOfSupply !== -1) {
      tempKeys.splice(indexOfSupply, 0, keyToMove);
      finalKeys = tempKeys;
    }
  }

  return finalKeys;
};

interface MarketDataGridProps {
  data: (MarketDataRow & { _blurState?: string })[];
  metadata: MarketFinderMetadata;
  isFreeUser: boolean;
  handleOnSampleClick;
  currency: string;
  selectedBudget: {
    type: 'buy' | 'rent';
    range: { min: number; max: number };
  };
  currentRequestType: 'free' | 'premium';
  openCreditDeductModal: () => void;
  selectedList:
    | {
        id: string;
        name: string;
        marketIDs: { [key: string]: 'saved' | 'saving' | null };
        marketDetails: any;
      }
    | 'loading';
  isLoggedIn: boolean;
  selectedListError: boolean;
  showSavedMarketLoadingError: boolean;
  setShowSavedMarketLoadingError: (isOpen: boolean) => void;
  handleSidebarClick?: any;
  handleSidebarListItemClick?: any;
  selectedSortBy: string;
  hidePagination?: boolean;
  hideSaveColumn?: boolean;
  hideUnlockButton?: boolean;
  userPickedColumnFields?: string[];
  hideBluredRows?: boolean;
}

const MarketDataGrid = ({
  data,
  metadata,
  isFreeUser,
  handleOnSampleClick,
  currency,
  selectedBudget,
  currentRequestType,
  openCreditDeductModal,
  selectedList,
  isLoggedIn,
  selectedListError,
  showSavedMarketLoadingError,
  setShowSavedMarketLoadingError,
  handleSidebarClick,
  handleSidebarListItemClick,
  selectedSortBy,
  hidePagination,
  hideSaveColumn,
  hideUnlockButton,
  userPickedColumnFields,
  hideBluredRows,
}: MarketDataGridProps) => {
  const currencySymbol = exchangeRates[currency].symbol;
  const columnsToShow = userPickedColumnFields ? userPickedColumnFields : [];
  let metadataKeys = getFilteredMetadataKeys(metadata, selectedSortBy, columnsToShow);
  const dispatch = useDispatch();
  const { lastSavedMarketNames, openSavedMarketsCTAModal } = useSelector(
    (state: any) => state.dashboard.savedMarkets
  );

  const user: User = useSelector((state: any) => state.dashboard.user);

  const userWantSavedMarketReDirectModalToStayClosed = localStorage.getItem(
    SAVED_MARKET_RE_DIRECTION_MODAL_CLOSED_KEY
  );
  const [confirmDeleteMarket, setConfirmDeleteMarket] = useState<{
    marketName: string;
    handleDelete: () => void;
  } | null>(null);

  const allSavedMarketLists = useSelector((state: any) => state.dashboard.allSavedMarketLists.data);
  const [openSaveMarketConfirmationModal, setOpenSaveMarketConfirmationModal] = useState(false);
  const [currentSelectedMarketObject, setCurrentSelectedMarketObject] = useState<{
    name: string;
    id: string;
    action: 'add' | 'remove';
    url: string;
  } | null>(null);

  // Define columns for DataGrid
  let columns: GridColDef[] = [
    {
      field: 'save',
      headerName: 'Save',
      renderHeader: () => (
        <Typography variant="inherit" component="span" sx={{ fontWeight: 'bold' }}>
          Save
        </Typography>
      ),
      width: 60,
      renderCell: (params: GridRenderCellParams) => (
        <Box display="flex" justifyContent="center" width="100%">
          <IconButton
            size="small"
            onClick={() => {
              if (!isLoggedIn) {
                handleOnSampleClick('expert');
                return;
              }

              if (selectedList !== 'loading' && selectedListError) {
                return;
              }

              if (selectedList === 'loading' || !selectedList.id) {
                console.warn('Selected list is not ready or ID is missing.');
                return;
              }

              const action =
                selectedList.marketIDs[params.row.market_id] === 'saved' ? 'remove' : 'add';

              if (
                action === 'add' &&
                isFreeUser &&
                filterExampleSavedMarkets(selectedList.marketDetails || {}).length >=
                  USER_WISE_SAVE_LIST_LIMIT[user.subscriptionPlan]
              ) {
                handleOnSampleClick('expert');
                return;
              }

              if (action === 'remove') {
                setConfirmDeleteMarket({
                  marketName: params.row.name,
                  handleDelete: () => {
                    dispatch(
                      updateMarketInList(
                        selectedList as {
                          id: string;
                          name: string;
                          marketIDs: { [key: string]: 'saved' | 'saving' | null };
                          marketDetails: any;
                        },
                        params.row.market_id,
                        action,
                        params.row.name
                      )
                    );
                  },
                });
                return;
              }

              if (allSavedMarketLists.length > 0) {
                setCurrentSelectedMarketObject({
                  name: params.row.name,
                  id: params.row.market_id,
                  action: action,
                  url: params.row.market_url,
                });
                setOpenSaveMarketConfirmationModal(true);
                return;
              }

              dispatch(
                updateMarketInList(
                  selectedList as {
                    id: string;
                    name: string;
                    marketIDs: { [key: string]: 'saved' | 'saving' | null };
                    marketDetails: any;
                  },
                  params.row.market_id,
                  action,
                  params.row.name
                )
              );
            }}
          >
            {selectedList === 'loading' ||
            selectedList.marketIDs[params.row.market_id] === 'saving' ? (
              <CircularProgress size={20} />
            ) : selectedList.marketIDs[params.row.market_id] === 'saved' ? (
              <Tooltip title="Remove from Saved Markets">
                <BookmarkIcon sx={{ fontSize: 20, color: COLOR_PRIMARY_2_800 }} />
              </Tooltip>
            ) : (
              <Tooltip title="Add to Saved Markets">
                <BookmarkBorderIcon sx={{ fontSize: 20 }} />
              </Tooltip>
            )}
          </IconButton>
        </Box>
      ),
    },
    {
      field: 'market_grade',
      headerName: 'Market Grade',
      renderHeader: () => (
        <Typography variant="inherit" component="span" sx={{ fontWeight: 'bold' }}>
          Market Grade
        </Typography>
      ),
      width: 110,
      sortable: false,
      type: 'string',
      renderCell: (params: GridRenderCellParams) => (
        <CellContent
          params={params}
          currency={currency}
          currencySymbol={currencySymbol}
          handleOnSampleClick={handleOnSampleClick}
        />
      ),
    },
    {
      field: 'name',
      headerName: 'Name',
      renderHeader: () => (
        <Typography variant="inherit" component="span" sx={{ fontWeight: 'bold' }}>
          Name
        </Typography>
      ),
      width: hideBluredRows ? 340 : 270,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <CellContent
          params={params}
          currency={currency}
          currencySymbol={currencySymbol}
          handleOnSampleClick={handleOnSampleClick}
        />
      ),
      headerAlign: 'left' as GridAlignment,
      align: 'left' as GridAlignment,
      type: 'string',
    },
    // Dynamically create columns from metadata
    ...metadataKeys.map(
      (key): GridColDef => {
        const columnMeta = metadata[key];
        const tooltipText = columnTooltips[key];
        return {
          field: key,
          headerName: columnMeta.display_name, // Use display_name for underlying header name (sorting/filtering)
          renderHeader: () =>
            tooltipText ? (
              <HeaderWithTooltip label={columnMeta.display_name} tooltip={tooltipText} />
            ) : (
              <Typography
                variant="inherit"
                component="span"
                sx={{ fontWeight: 'bold', paddingRight: '30px' }}
              >
                {columnMeta.display_name}
              </Typography>
            ),
          width: columnMeta.width ?? 170, // Use width from metadata if available, otherwise default to 170
          sortable: false,
          renderCell: (params: GridRenderCellParams) => (
            <CellContent
              params={params}
              columnMeta={columnMeta}
              currency={currency}
              currencySymbol={currencySymbol}
              handleOnSampleClick={handleOnSampleClick}
            />
          ),
          headerAlign: 'left' as GridAlignment,
          align: 'left' as GridAlignment,
          // Add type for potential filtering/sorting improvements
          type:
            columnMeta.data_type === 'number' || columnMeta.data_type === 'integer'
              ? 'number'
              : 'string',
        };
      }
    ),
  ].filter(column => !(column.field === 'save' && hideSaveColumn));

  // Filter out specific columns if hideBluredRows is true
  if (hideBluredRows) {
    const columnsToHide = ['revenue', 'yoy_supply_change', 'yoy_revenue_change'];
    columns = columns.filter(column => !columnsToHide.includes(column.field));
  }

  // Prepare rows for DataGrid, ensuring each has a unique 'id'
  const rows: GridRowsProp = data.map((row, index) => ({
    id: row.rank ?? `row-${index}`, // Use rank as ID, fallback to index + prefix for uniqueness
    ...row,
  }));

  return (
    <>
      {showSavedMarketLoadingError && (
        <Alert
          severity="error"
          onClose={() => setShowSavedMarketLoadingError(false)}
          sx={{ mb: '10px' }}
        >
          Failed to fetch your saved markets. Please try to reload the page.
        </Alert>
      )}
      <Box
        component={Paper}
        elevation={0}
        sx={{
          position: 'relative',
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          mt: 2,
          width: '100%',
          height: 'auto',
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          autoHeight
          disableSelectionOnClick
          density="standard"
          disableColumnMenu
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: COLOR_GREY_800 + '88',
              borderBottom: '1px solid ' + COLOR_GREY_600,
              color: 'text.primary',
            },
            // Ensure header titles are bold (already handled by renderHeader, but good fallback)
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 'bold',
            },
            // Ensure header text alignment
            '& .MuiDataGrid-columnHeaderTitleContainer': {
              justifyContent: 'flex-start',
              padding: 0,
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid #e0e0e0',
              padding: '12px 16px',
              color: 'text.primary',
              // verticalAlign: 'top',
              lineHeight: 'normal',
            },
            '& .MuiDataGrid-row': {
              backgroundColor: '#ffffff',
              '&:hover': {
                backgroundColor: '#f9f9f9',
              },
              // Remove bottom border from the last row
              //  '&:last-child .MuiDataGrid-cell': {
              //      borderBottom: 'none',
              //  },
            },
            // Style the virtual scroller if height is constrained
            '& .MuiDataGrid-virtualScroller': {
              // maxHeight: '450px',
              overflowY: 'auto',
            },
            // Remove focus outline on cells
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },
            // Remove focus outline on column headers
            '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
              outline: 'none',
            },
            '& .MuiDataGrid-footerContainer': {
              display: hidePagination ? 'none' : 'flex',
            },
          }}
        />

        {!hideUnlockButton && (isFreeUser || currentRequestType === 'free') && (
          <BlurCTA
            handleOnSampleClick={isFreeUser ? handleOnSampleClick : openCreditDeductModal}
            totalRows={rows ? rows.length : 1}
          />
        )}
      </Box>

      {userWantSavedMarketReDirectModalToStayClosed !== 'true' && (
        <SavedMarketsCTAModal
          open={typeof openSavedMarketsCTAModal === 'string'}
          onClose={() => {
            dispatch(setOpenSavedMarketsCTAModalAction(false));
            localStorage.setItem(SAVED_MARKET_RE_DIRECTION_MODAL_CLOSED_KEY, 'true');
          }}
          marketName={typeof openSavedMarketsCTAModal === 'string' ? openSavedMarketsCTAModal : ''}
          handleSidebarClick={handleSidebarClick}
          handleSidebarListItemClick={handleSidebarListItemClick}
        />
      )}

      {confirmDeleteMarket && (
        <ConfirmationModal
          open={confirmDeleteMarket !== null}
          onClose={() => setConfirmDeleteMarket(null)}
          title={
            <>
              Are you sure you want to remove{' '}
              <span style={{ color: COLOR_PRIMARY_2 }}>{confirmDeleteMarket.marketName}</span> from
              your Saved Markets list?
            </>
          }
          confirmButtonText="Yes"
          onConfirm={() => confirmDeleteMarket.handleDelete()}
          onCancel={() => setConfirmDeleteMarket(null)}
          cancelButtonText="No"
        />
      )}

      {openSaveMarketConfirmationModal && (
        <SaveMarketConfirmationModal
          open={openSaveMarketConfirmationModal}
          onClose={() => setOpenSaveMarketConfirmationModal(false)}
          currentSelectedMarketObject={currentSelectedMarketObject}
          handleOnSampleClick={handleOnSampleClick}
        />
      )}
    </>
  );
};

const SaveMarketConfirmationModal = ({
  open,
  onClose,
  currentSelectedMarketObject,
  handleOnSampleClick,
}: {
  open: boolean;
  onClose: () => void;
  currentSelectedMarketObject: {
    name: string;
    id: string;
    action: 'add' | 'remove';
    url: string;
  } | null;
  handleOnSampleClick: (plan: string) => void;
}) => {
  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          border: 'none',
          outline: 'none',
          boxShadow: 24,
          p: '10px',
          borderRadius: '12px',
          width: '90vw',
          maxWidth: '400px',
        }}
      >
        <SavedMarketListsDropdown
          mode="saveListinMarketExplorer"
          currentSelectedMarketObject={currentSelectedMarketObject}
          handleOnSampleClick={handleOnSampleClick}
          handleOutSideClose={onClose}
        />
      </Box>
    </Modal>
  );
};

const SavedMarketsCTAModal = ({
  open,
  onClose,
  marketName,
  handleSidebarClick,
  handleSidebarListItemClick,
}: {
  open: boolean;
  onClose: () => void;
  marketName: string;
  handleSidebarClick?: any;
  handleSidebarListItemClick?: any;
}) => {
  const history = useHistory();

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          maxWidth: '90%',
          bgcolor: 'background.paper',
          border: 'none',
          outline: 'none',
          boxShadow: 24,
          p: 4,
          borderRadius: '12px',
          textAlign: 'center',
          color: 'text.primary',
        }}
      >
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'text.secondary',
          }}
        >
          <CloseIcon />
        </IconButton>
        <Typography
          id="saved-market-modal-title"
          variant="h6"
          component="h2"
          sx={{ mb: 1, fontWeight: 'bold' }}
        >
          <span style={{ color: COLOR_PRIMARY_2_800 }}>{marketName}</span> is now on your Saved
          Markets
        </Typography>
        <Typography id="saved-market-modal-description" sx={{ mb: 3 }}>
          Compare how{' '}
          <span style={{ color: COLOR_PRIMARY_2_800, fontWeight: 'bold' }}>{marketName}</span>{' '}
          stacks up against your other saved markets. Spot regulations, seasonality, and YoY
          performance trends in one view.
        </Typography>
        <Button
          variant="contained"
          onClick={() => {
            handleSidebarClick && handleSidebarClick('main');
            handleSidebarListItemClick && handleSidebarListItemClick(0);
          }}
          fullWidth
          sx={{
            backgroundColor: COLOR_PRIMARY_2_800,
            textTransform: 'none',
            padding: '10px 20px',
            fontWeight: 'bold',
            color: '#fff',
          }}
        >
          View on Saved Markets
        </Button>
      </Box>
    </Modal>
  );
};

export default MarketDataGrid;
